import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get("next") || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success(t("auth.login_success"));
      navigate(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("auth.login_error"));
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (e, mail) => {
    e.preventDefault();
    setEmail(mail); setPassword("demo1234");
  };

  return (
    <AuthLayout>
      <h1 className="font-display text-3xl">{t("auth.login_title")}</h1>
      <p className="text-sm text-muted-foreground mt-1">{t("auth.login_sub")}</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label>{t("auth.login_email")}</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)}
            type="email" required data-testid="login-email" className="h-11" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.login_password")}</Label>
          <Input value={password} onChange={(e) => setPassword(e.target.value)}
            type="password" required data-testid="login-password" className="h-11" />
        </div>
        <Button type="submit" size="lg" disabled={busy}
          data-testid="login-submit" className="w-full rounded-full h-12">
          {busy ? t("auth.login_loading") : t("auth.login_submit")}
        </Button>
      </form>

      <div className="mt-5 text-sm text-muted-foreground">
        {t("auth.no_account")}{" "}
        <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`}
          className="text-primary hover:underline" data-testid="link-register">
          {t("auth.create_account_link")}
        </Link>
      </div>

      <div className="mt-8 pt-6 border-t border-border">
        <div className="overline text-muted-foreground mb-3">{t("auth.demo_label")}</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={(e) => quickFill(e, "lea@vintedstyle.be")} data-testid="demo-lea"
            className="text-xs px-3 h-8 rounded-full bg-accent text-accent-foreground hover:bg-secondary transition-colors">
            Léa (Bruxelles)
          </button>
          <button onClick={(e) => quickFill(e, "sarah@vintedstyle.be")} data-testid="demo-sarah"
            className="text-xs px-3 h-8 rounded-full bg-accent text-accent-foreground hover:bg-secondary transition-colors">
            Sarah (Anvers)
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-[calc(100vh-4rem)] grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.pexels.com/photos/38273589/pexels-photo-38273589.jpeg?auto=compress&cs=tinysrgb&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/40 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-primary-foreground">
          <div className="overline">vintedstyle.be</div>
          <div className="font-display text-3xl mt-2 max-w-sm leading-tight text-white drop-shadow">
            {t("auth.auth_quote")}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm fade-in">{children}</div>
      </div>
    </div>
  );
}
